/* eslint no-shadow: ["error", { "allow": ["state"] }] */
/* eslint-disable no-param-reassign */

import {
  get,
  pick,
  cloneDeep,
  set,
  uniq,
  uniqBy,
} from 'lodash-es';

import catchify from 'catchify';
import toast from '../../../components/toast/toast';
import sportradarModule from '../sportradar/sportradar';
import SportradarService from '../../../services/sportradar';

import Analytics from '../../../utils/analytics';

import Conduit from '../../../services/conduitEventsService';
import Productions from '../../../services/productions';
import Graphics from '../../../services/graphics';
import Pusher from '../../../services/pusher';
import ShowsService from '../../../services/shows';
import { getMultiple as getMultipleAssets } from '../../../services/assets';
import { getMultipleThemes } from '../../../services/themes';

import Reactive from '../../../utils/reactive';

import {
  shortenPanelUrls,
  shortenGraphicUrls,
  shortenThemeUrls,
} from '../../../utils/shortener';

import sendToPreviewIframes from '../../../utils/live-preview-messaging';

import {
  // Constants
  SettingsFields,

  // Helpers
  cleanNestedPosts,
  dedupeTimelineItems,
  diffpatcher,
  getItemFromTimeline,
  isAlreadyQueued,
  determineSubIndex,
  validateTimelineItem,
  getPreviousItemOrPost,
  getNextItemOrPost,
  syncQueuedPost,
  isTheme,
  isPlainObject,
  isIdOnlyPostPlaceholder,
  getSocialPosts,

  // Set/update prevProduction for tracking changes
  hasPendingChanges,
  setPrevProduction,
  updatePrevProduction,

  // Socket emitters
  updateTimeline,
  sendSettings,
} from '../../../utils/production';

import { collectPartialLookupEntries } from '../../../utils/timeline-lookup-prefill';

import { flattenShowItems, collectAssetIds, collectThemeIds } from '../../../utils/showToTimeline';

import { normalizePostForEvents } from '../../../utils/helpers';

const clone = (obj) => JSON.parse(JSON.stringify(obj));
const isContainerLayout = (layout) => ['graphics', 'segment'].includes(layout);

/**
 * Wait (bounded) for the socket to connect. Used when adding a Show to the
 * timeline right after a fresh page load, where the socket handshake may
 * still be in flight when the caller is ready.
 * @returns {Promise<Boolean>} Whether the socket connected within the timeout.
 */
const waitForSocketConnected = (rootState, timeoutMs = 5000) => new Promise((resolve) => {
  if (rootState.clients.isSocketConnected) {
    resolve(true);
    return;
  }

  const start = Date.now();
  const interval = setInterval(() => {
    if (rootState.clients.isSocketConnected) {
      clearInterval(interval);
      resolve(true);
    } else if (Date.now() - start >= timeoutMs) {
      clearInterval(interval);
      resolve(false);
    }
  }, 100);
});

const sportradarTrackingRequests = new WeakMap();

function getSportradarTrackingItems(timeline = []) {
  const items = new Map();
  function collect(list) {
    list.forEach((item) => {
      if (item?.layout === 'graphic' && item.timeline_id && item.template_id) {
        // Element counts can be stale; inspect the actual template instead.
        items.set(item.timeline_id, item);
      }

      if (Array.isArray(item?.posts)) {
        collect(item.posts);
      }
    });
  }

  collect(timeline);
  return items;
}

function getSportradarSelectionKey(timeline) {
  return JSON.stringify([...getSportradarTrackingItems(timeline).values()].map((item) => [
    item.timeline_id,
    item.template_id,
    Object.entries(item.fields || {}).map(([id, fields]) => [
      id, pick(fields?.data, ['sport', 'gameId']),
    ]).filter(([, data]) => Object.keys(data).length),
  ]));
}

function getSportradarGames(elements, fields = {}) {
  const games = new Map();
  function collect(list) {
    list.forEach((element) => {
      if (element?.type === 'sportradar') {
        // Match Live's shallow data merge. Explicit null/empty overrides clear a selection.
        const { sport, gameId } = { ...element.data, ...fields?.[element.id]?.data };
        if (typeof sport === 'string' && sport.length && typeof gameId === 'string' && gameId.length) {
          games.set(`${sport.toLowerCase()}:${gameId}`, { sport: sport.toLowerCase(), gameId });
        }
      }

      if (Array.isArray(element?.elements)) {
        collect(element.elements);
      }
    });
  }

  collect(elements);
  return [...games.values()];
}

function setSportradarSubscriptions(state, next) {
  function channels(items) {
    return new Map(Object.values(items || {}).flatMap((item) => (
      item.games.map((game) => [`${game.sport}:${game.gameId}`, game])
    )));
  }

  const previousChannels = channels(state.activeSportradarInGameElements);
  const nextChannels = channels(next);
  nextChannels.forEach(({ sport, gameId }, key) => {
    if (!previousChannels.has(key)) {
      SportradarService.subscribeToGameChannel(sport, gameId);
    }
  });

  previousChannels.forEach(({ sport, gameId }, key) => {
    if (!nextChannels.has(key)) {
      SportradarService.unsubscribeFromGameChannel(sport, gameId);
    }
  });

  state.activeSportradarInGameElements = next;
}

/**
 * A timeline item is only valid if it has at least one key. Empty objects
 * (`{}`) are corruption left behind by race conditions in socket delta
 * merging; they're truthy so `filter(Boolean)` keeps them, but they break
 * consumers that read `layout`, `fields`, or `meta` off nested items.
 * @param {*} item - Candidate timeline item
 * @returns {Boolean}
 */
const isValidTimelineItem = (item) => item && Object.keys(item).length > 0;

function hasTimelineItem(timelineId, items = [], nested = false) {
  return items.some((item) => (
    !(nested && isIdOnlyPostPlaceholder(item))
    && (item?.timeline_id === timelineId
      || hasTimelineItem(timelineId, item?.posts || [], true))
  ));
}

function removeTimelineItemFromContainers(timelineId, items = [], nested = false) {
  const directIndex = items.findIndex((item) => (
    item?.timeline_id === timelineId && !(nested && isIdOnlyPostPlaceholder(item))
  ));
  if (directIndex !== -1) {
    items.splice(directIndex, 1);
    return true;
  }

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const posts = item?.posts || [];

    const nestedIndex = posts.findIndex((child) => (
      child?.timeline_id === timelineId && !isIdOnlyPostPlaceholder(child)
    ));
    if (nestedIndex !== -1) {
      posts.splice(nestedIndex, 1);
      return true;
    }

    if (removeTimelineItemFromContainers(timelineId, posts, true)) {
      return true;
    }
  }

  return false;
}

const findParentContainer = (timelineId, items = []) => {
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const posts = item?.posts || [];

    if (posts.some((child) => (
      child?.timeline_id === timelineId && !isIdOnlyPostPlaceholder(child)
    ))) {
      return item;
    }

    const nested = findParentContainer(timelineId, posts);
    if (nested) {
      return nested;
    }
  }

  return null;
};

/**
 * Remove a timeline item from the current production state by id.
 * Clears live index/sub_index when the removed item (or a descendant) was live.
 * An emptied parent container is intentionally left in place (matching
 * REMOVE_LAYOUT) so it can keep accepting drops.
 * @param {Object} state - Production module state.
 * @param {String} timelineId - ID of the item to remove.
 */
const removeTimelineItemById = (state, timelineId) => {
  const removedItem = getItemFromTimeline(timelineId, state.production.timeline);

  const removedLiveItem = Boolean(
    removedItem
    && state.production.index
    && hasTimelineItem(state.production.index, [removedItem]),
  );

  removeTimelineItemFromContainers(timelineId, state.production.timeline);

  if (removedLiveItem) {
    state.production.index = null;
    state.production.sub_index = null;
  }
};

/**
 * Move a timeline item to a new container (or the root) at the given index.
 * The item is located recursively, so this works for items nested inside
 * segments/groups as well as root items.
 * @param {Object} state - Production module state.
 * @param {Object} payload - Move descriptor.
 * @param {String} payload.timelineId - ID of the item to move.
 * @param {String|null} payload.parentTimelineId - New parent id (null for root).
 * @param {Number} [payload.index] - Target index within the new container.
 * @returns {String} Where the item ended up: 'parent', 'root', or 'missing'.
 */
const moveTimelineItemById = (state, payload) => {
  const { timelineId, parentTimelineId, index } = payload;
  const item = getItemFromTimeline(timelineId, state.production.timeline);

  if (!item) {
    return 'missing';
  }

  // Detach from its current container first.
  removeTimelineItemFromContainers(timelineId, state.production.timeline);

  const target = parentTimelineId
    ? getItemFromTimeline(parentTimelineId, state.production.timeline)
    : null;

  if (parentTimelineId && !target) {
    // Parent isn't present yet (e.g. its add event hasn't arrived). Put the
    // item back at the root end so it isn't lost; an order event will fix it.
    if (!state.production.timeline) {
      state.production.timeline = [];
    }

    state.production.timeline.push(item);
    return 'root';
  }

  if (target) {
    if (!target.posts) {
      target.posts = [];
    }

    if (typeof index === 'number' && index >= 0 && index <= target.posts.length) {
      target.posts.splice(index, 0, item);
    } else {
      target.posts.push(item);
    }

    return 'parent';
  }

  if (!state.production.timeline) {
    state.production.timeline = [];
  }

  if (typeof index === 'number' && index >= 0 && index <= state.production.timeline.length) {
    state.production.timeline.splice(index, 0, item);
  } else {
    state.production.timeline.push(item);
  }

  return 'root';
};

function setNewTimelineIds(item) {
  if (!item) {
    return;
  }

  item.timeline_id = crypto.randomUUID();

  if (Array.isArray(item.posts)) {
    item.posts.forEach((post) => {
      if (item.layout === 'graphic' && isIdOnlyPostPlaceholder(post)) {
        post.timeline_id = item.timeline_id;
      } else {
        setNewTimelineIds(post);
      }
    });
  }
}

const getNestedItems = (item) => {
  if (!item?.posts?.length) {
    return [];
  }

  return item.posts.reduce((memo, post) => (
    memo.concat(post, getNestedItems(post))
  ), []);
};

/**
 * Merge two adjacent graphics/graphic groups at i1/i2 into a single group.
 * The resulting group always lands at the upper of the two indices (i1).
 * @param {Array} items - Timeline array (root timeline or container posts)
 * @param {Number} i1 - Upper index
 * @param {Number} i2 - Lower index (adjacent to i1)
 * @returns {Boolean} - True if a graphics merge was performed
 */
const mergeGraphicsIntoGroup = (items, i1, i2) => {
  const g1 = clone(items[i1]);
  const g2 = clone(items[i2]);

  // Ensure elements are actually graphics or groups, in case something changed
  if (!['graphic', 'graphics'].includes(g1?.layout)) {
    return false;
  }

  if (!['graphic', 'graphics'].includes(g2?.layout)) {
    return false;
  }

  if ((g1.layout === 'graphics' && g2.layout === 'graphic') || (g1.layout === 'graphic' && g2.layout === 'graphics')) {
    // Add graphic to existing group, remove leftover graphic
    if (g1.layout === 'graphics') {
      // Append graphic 2 to group 1
      g1.posts.push(g2);
      items.splice(i1, 1, g1);
      items.splice(i2, 1);
    } else {
      // Prepend graphic 1 to group 2
      g2.posts.unshift(g1);
      items.splice(i2, 1, g2);
      items.splice(i1, 1);
    }

    return true;
  }

  if (g1.layout === 'graphics' && g2.layout === 'graphics') {
    // Merge two different groups together, taking group 1 properties
    g1.posts = g1.posts.concat(g2.posts);
    items.splice(i1, 1, g1);
    items.splice(i2, 1);
    return true;
  }

  // Two single graphics, create a new group and add both
  // New group is inserted at i1
  items.splice(i1, 1);
  items.splice(i2 > i1 ? i2 - 1 : i2, 1);

  const numGroups = items.filter((i) => i.layout === 'graphics').length;
  const groupLabel = 'Custom Scene';

  const item = {
    title: `${groupLabel} ${numGroups + 1}`,
    timeline_id: crypto.randomUUID(),
    owner: g1.owner,
    displayTime: g1.displayTime,
    transition: g1.transition,
    layout: 'graphics',
    posts: [g1, g2],
  };

  // Insert new component
  items.splice(i1, 0, item);
  return true;
};

/**
 * Merge two adjacent timeline items at i1/i2 into a segment.
 * Anything can live inside a segment except other segments, so:
 * - If the source (context menu owner) is a segment, absorb the other item
 *   (or its posts when it is also a segment, keeping the source's name).
 * - Otherwise, if the adjacent item is a segment, move the source into it.
 * - If neither is a segment, wrap both in a brand new segment.
 * The resulting segment always lands at the upper of the two indices (i1).
 * @param {Array} timeline - Root production timeline
 * @param {Number} i1 - Upper index
 * @param {Number} i2 - Lower index (adjacent to i1)
 * @param {Object} production - Production state (used for default transition)
 * @param {String} sourceTimelineId - timeline_id of the item whose context menu was opened
 */
const mergeIntoSegment = (timeline, i1, i2, production, sourceTimelineId) => {
  const upper = timeline[i1];
  const lower = timeline[i2];

  if (!upper || !lower) {
    return;
  }

  const sourceIsUpper = upper.timeline_id === sourceTimelineId;
  const source = sourceIsUpper ? upper : lower;
  const target = sourceIsUpper ? lower : upper;

  if (source.layout === 'segment') {
    if (target.layout === 'segment') {
      // Merge two segments together, keeping the source segment's name
      source.posts = sourceIsUpper
        ? (source.posts || []).concat(target.posts || [])
        : (target.posts || []).concat(source.posts || []);
    } else if (sourceIsUpper) {
      // Source segment above a single item, append the item
      source.posts = (source.posts || []).concat(target);
    } else {
      // Source segment below a single item, prepend the item
      source.posts = [target].concat(source.posts || []);
    }

    timeline.splice(i1, 1, source);
    timeline.splice(i2, 1);
    return;
  }

  if (target.layout === 'segment') {
    // Move the source item into the adjacent segment
    if (sourceIsUpper) {
      target.posts = [source].concat(target.posts || []);
    } else {
      target.posts = (target.posts || []).concat(source);
    }

    timeline.splice(i1, 1, target);
    timeline.splice(i2, 1);
    return;
  }

  // Neither item is a segment, wrap both in a new one,
  // naming it after the existing segment pattern
  const numSegments = timeline.filter((i) => i.layout === 'segment').length;

  const newSegment = {
    title: `Segment ${numSegments + 1}`,
    timeline_id: crypto.randomUUID(),
    displayTime: source.displayTime || 5e3,
    transition: source.transition || production.transition,
    layout: 'segment',
    posts: [upper, lower],
  };

  timeline.splice(i1, 1);
  timeline.splice(i2 - 1, 1);
  timeline.splice(i1, 0, newSegment);
};

/**
 * Apply partial deltas to production state instead of the legacy array-index-based delta.
 * Partial deltas use timeline_id-based keys, which eliminates race conditions
 * from concurrent edits that can introduce null entries in array-index-based merging.
 * @param {Object} state - Vuex state
 * @param {Object} payload - Socket payload containing partialDeltas
 */
const applyPartialDeltas = (state, payload) => {
  const { partialDeltas } = payload;
  if (!partialDeltas) { return; }

  const { productionDelta, timelineDelta, timelineItemDeltas } = partialDeltas;

  // 1. Apply production-level changes (settings, metadata, etc.)
  if (productionDelta) {
    diffpatcher.patch(state.production, productionDelta);
  }

  // 2. Reconstruct timeline using timeline_id-based map to avoid array index mismatches
  if (timelineDelta) {
    const currentTimelineIds = (state.production.timeline || []).map((i) => i.timeline_id);
    diffpatcher.patch(currentTimelineIds, timelineDelta);

    const timelineMap = {};
    (state.production.timeline || []).forEach((item) => {
      if (item?.timeline_id) {
        // Apply per-item delta if one exists before storing in the map
        if (timelineItemDeltas?.[item.timeline_id]) {
          diffpatcher.patch(item, timelineItemDeltas[item.timeline_id]);
        }

        timelineMap[item.timeline_id] = item;
      }
    });

    state.production.timeline = currentTimelineIds.map((id) => {
      if (timelineMap[id]) return timelineMap[id];
      if (timelineItemDeltas?.[id]) {
        return diffpatcher.patch({}, timelineItemDeltas[id]);
      }

      return null;
    }).filter((item) => item && Object.keys(item).length > 0);
  } else if (timelineItemDeltas) {
    // No structural changes, just apply per-item deltas matched by timeline_id
    Object.keys(timelineItemDeltas).forEach((timelineId) => {
      const item = state.production.timeline?.find((t) => t?.timeline_id === timelineId);
      if (item) {
        diffpatcher.patch(item, timelineItemDeltas[timelineId]);
      }
    });
  }

  // Race conditions in array-index-based delta merging can inject empty
  // objects (`{}`) into group/segment posts arrays. Filter them out here so
  // corrupt entries never persist or reach consumers (SET_TIMELINE also runs
  // this cleanup, but doing it at the source keeps state clean even if
  // applyPartialDeltas is reused elsewhere).
  cleanNestedPosts(state.production.timeline || []);
};

const state = {
  loading: false,
  loadingMore: false,

  duration: 0,
  videoDuration: 0,
  production: {},

  total: 0,
  productions: [],
  flaggedProductions: [],
  deletedProductions: [],

  loadedDeletedProductions: false,
  selectedProductionId: null,
  tagsForUser: [],
  clientsInDetailedProduction: null,
  saving: false,
  lastUpdated: null,

  // Track timeline items with SR game elements
  activeSportradarInGameElements: {},

  // Pagination
  page: 1,
  limit: 50,
  owner: null,
};

const actions = {
  setOwner(context, owner) {
    context.commit('SET_OWNER', owner);
  },

  /**
   * Reset all lists of productions to nothing, and reset page number to 1.
   */
  clearProductions(context) {
    context.commit('CLEAR_PRODUCTIONS');
    context.commit('SET_PAGE', 1);
  },

  clearProduction(context) {
    // Make sure to leave the socket room before clearing the production.
    // Otherwise we'll lose the ID and be stuck in the room
    $socket.emit('leave', { production_id: context.state.production._id });

    context.commit('CLEAR_PRODUCTION');
  },

  setTimelineField(context, payload) {
    const previousSelection = getSportradarSelectionKey(context.state.production.timeline);
    context.commit('SET_TIMELINE_FIELD', payload);
    if (previousSelection !== getSportradarSelectionKey(context.state.production.timeline)) {
      return context.dispatch('trackSportradarInGameElement');
    }

    return null;
  },

  setTimelineTitle(context, payload) {
    context.commit('SET_TIMELINE_TITLE', payload);
  },

  /**
   * Replace playlist item at index with provided elements(s)
   */
  replacePlaylistItem(context, payload) {
    context.commit('REPLACE_PLAYLIST_ITEM', payload);
  },

  /**
   * Replace playlist item at index withing a group with provided elements(s)
   */
  replaceGroupItem(context, payload) {
    context.commit('REPLACE_GROUP_ITEM', payload);
  },

  socket_clientsInProduction({ commit }, payload) {
    commit('UPDATE_CLIENTS_IN_DETAILED_PRODUCTION', payload.clientList);
  },

  async socket_reconnect(context) {
    const { _id: productionId } = context.state.production || {};

    if (productionId) {
      // Start by refetching the production from the API
      // jic anything was missed while disconnected.
      await context.dispatch('getProduction');

      // If anything like themes, social posts, etc.
      // were added while disconnected, then we need to
      // load them into state.
      await context.dispatch('getTimelineItems', {
        skipShortUrlGeneration: true,
      });

      // Then, rejoin the socket room for the production to get live changes.
      // We do this second so the user doesn't push any more deltas while the production
      // is loading from the API.
      $socket.emit('production', { production_id: productionId });
      return;
    }

    // Dashboard status subscriptions are tied to the previous socket connection.
    // Refresh every loaded production so the new socket rejoins those rooms and
    // replaces any presence changes missed while disconnected.
    context.dispatch('refreshConnectedScreens');
  },

  async socket_timeline({ state, commit, dispatch }, payload) {
    if ('preview' in payload) {
      // All we really care about is what timeline item is set to preview
      // So this will make sure preview.index and preview.sub_index are set
      commit('PREVIEW_SET_SUB_INDEX', payload);
      sendToPreviewIframes('timeline', payload, { $frameTarget: true, live: false, preview: true });
      return;
    }

    const isNewDelta = payload.version === 2 && (payload.delta || payload.partialDeltas);

    // If user has pending changes,
    // send them before processing partialDeltas received from another client.
    // New-client partialDeltas don't need a flush because the accompanying
    // targeted events already represent the authoritative changes.
    if (!isNewDelta) {
      const pendingChanges = hasPendingChanges(state.production);
      if ((payload.partialDeltas || payload.delta) && state.production && pendingChanges) {
        dispatch('updateTimeline');
      }
    }

    const previousSelection = getSportradarSelectionKey(state.production.timeline);
    // Merge any deltas into current state. New-client deltas are accompanied
    // by targeted timeline events, so strip delta/partialDeltas to avoid
    // double-applying the same changes.
    commit('SET_TIMELINE', isNewDelta
      ? { ...payload, delta: undefined, partialDeltas: undefined }
      : payload);

    if (previousSelection !== getSportradarSelectionKey(state.production.timeline)) {
      dispatch('trackSportradarInGameElement');
    }

    const { timeline, index, sub_index: subIndex } = state.production;

    // Sync `queuedPost` on current live item if necessary
    if (syncQueuedPost({ timeline, index, subIndex })) {
      // `queuedPost` was updated, so make sure that change is sent to other clients
      dispatch('updateTimeline');
    }

    // Load any missing timeline items
    await dispatch('getTimelineItems', {
      skipShortUrlGeneration: true,
    });

    // Forward timeline update to preview/live iframes so they stay in sync
    sendToPreviewIframes('timeline', payload, { $frameTarget: true, live: true, preview: false });
  },

  socket_timelineSettings(context, payload) {
    const mutation = payload.preview ? 'PREVIEW_TIMELINE_SETTINGS' : 'TIMELINE_SETTINGS';
    context.commit(mutation, payload);
    const isPreview = !!payload.preview;
    sendToPreviewIframes('timelineSettings', payload, { $frameTarget: true, live: !isPreview, preview: isPreview });
  },

  socket_updateTimelineItem({ state, commit }, payload) {
    commit('SOCKET_UPDATE_TIMELINE_ITEM', payload);

    setPrevProduction(state.production);
    sendToPreviewIframes('updateTimelineItem', payload, { $frameTarget: true, live: true, preview: false });
  },

  async socket_addTimelineItem({ state, commit, dispatch }, payload) {
    commit('SOCKET_ADD_TIMELINE_ITEM', payload);

    setPrevProduction(state.production);

    // Newly added items may not have their metadata loaded into lookup tables,
    // so fetch missing themes/templates/posts/etc. before rendering.
    await dispatch('getTimelineItems', { skipShortUrlGeneration: true });
    sendToPreviewIframes('addTimelineItem', payload, { $frameTarget: true, live: true, preview: false });
  },

  socket_removeTimelineItem({ state, commit }, payload) {
    commit('SOCKET_REMOVE_TIMELINE_ITEM', payload);

    setPrevProduction(state.production);
    sendToPreviewIframes('removeTimelineItem', payload, { $frameTarget: true, live: true, preview: false });
  },

  socket_removeTimelineItems({ state, commit }, payload) {
    commit('SOCKET_REMOVE_TIMELINE_ITEMS', payload);

    setPrevProduction(state.production);
    sendToPreviewIframes('removeTimelineItems', payload, { $frameTarget: true, live: true, preview: false });
  },

  socket_updateTimelineOrder({ state, commit }, payload) {
    commit('SOCKET_UPDATE_TIMELINE_ORDER', payload);

    setPrevProduction(state.production);
    sendToPreviewIframes('updateTimelineOrder', payload, { $frameTarget: true, live: true, preview: false });
  },

  socket_moveTimelineItem({ state, commit }, payload) {
    commit('SOCKET_MOVE_TIMELINE_ITEM', payload);

    setPrevProduction(state.production);
    sendToPreviewIframes('moveTimelineItem', payload, { $frameTarget: true, live: true, preview: false });
  },

  socket_startPlayback(context) {
    context.commit('SET_VIDEO_PLAYING', true);
    sendToPreviewIframes('startPlayback', { $frameTarget: true, live: true, preview: false });
  },

  socket_stopPlayback(context) {
    context.commit('SET_VIDEO_PLAYING', false);
    sendToPreviewIframes('stopPlayback', { $frameTarget: true, live: true, preview: false });
  },

  socket_playbackDuration(context, duration) {
    context.commit('SET_DURATION', duration);
    sendToPreviewIframes('playbackDuration', duration, { $frameTarget: true, live: true, preview: false });
  },

  socket_videoDuration(context, payload) {
    const { duration, preview } = payload;

    if (preview) {
      context.commit('SET_PREVIEW_VIDEO_DURATION', duration);;
    } else {
      context.commit('SET_VIDEO_DURATION', duration);
    }

    const isPreview = !!payload.preview;
    sendToPreviewIframes('videoDuration', payload, { $frameTarget: true, live: !isPreview, preview: isPreview });
  },

  /**
   * Populate lookup tables with partial metadata carried by the timeline items
   * themselves. Items added from sources that only carry lightweight metadata
   * (e.g. Global Search) can render their name/thumbnail immediately instead of
   * flashing the "deleted" fallback while the full metadata loads from the API.
   *
   * Entries are only filled when the lookup table has no entry (or only a
   * partial entry for polls), so existing full data is never overwritten. The
   * per-resource `getTimeline*` fetchers still run afterwards and replace the
   * partial entries with the full records, since they key off fields that a
   * partial entry doesn't have (`settings`, `elements`, etc.).
   */
  addTimelineItemsToLookupTables({ rootState, commit }) {
    const { timeline } = rootState?.production?.production || {};
    if (!Array.isArray(timeline)) {
      return;
    }

    collectPartialLookupEntries(timeline, rootState).forEach(({ mutation, entry }) => {
      commit(mutation, [entry]);
    });
  },

  /**
   * Call out to other functions that are responsible for grabbing all items
   * from the timeline and ensuring the necessary data is loaded from the API
   * and stored in their respective lookup tables.
   * Otherwise, items in the playlist could show up as "deleted".
   * Methods should not refetch data that we already have, so it should be safe to call multiple times.
   * NOTE: Instead of manually calling this in various places where we might forget, we can probably just watch for `timeline` changes.
   */
  async getTimelineItems({ dispatch }, opts = {}) {
    const {
      skipShortUrlGeneration = false,
      timelineIds, // List of timeline items to specifically check for QR code URLs
    } = opts;

    // Fill lookup tables with any partial metadata the timeline items already
    // carry (e.g. items just added from Global Search), so newly added items
    // render immediately instead of flashing the "deleted" fallback while the
    // full metadata below loads. Must run before the async fetches below.
    dispatch('addTimelineItemsToLookupTables');

    await Promise.allSettled([
      dispatch('getTimelineThemes'),
      dispatch('getTimelinePanels'),
      dispatch('getTimelineTemplates'),
      dispatch('getTimelineTagboards'),
      dispatch('getTimelinePosts'),
      dispatch('assets/getTimelineAssets'),
      dispatch('polls/getTimelinePolls'),
    ]).then((results) => {
      results.forEach((result) => {
        if (result.status === 'rejected') {
          console.error('Error fetching timeline items:', result);
        }
      });
    });

    // Unless skipped, make sure any QR codes have short URLs generated.
    if (!skipShortUrlGeneration) {
      if (timelineIds) {
        await dispatch('generateShortUrls', timelineIds);
      } else {
        // Attempt to check every timeline item for missing shortLinks that need to be populated.
        await dispatch('generateShortUrls');
      }
    }
  },

  advance(context, direction) {
    const {
      timeline,
      index,
      sub_index: subIndex,
      looped,
    } = context.state.production;

    const currentItem = getItemFromTimeline(index, timeline);

    if (!currentItem) {
      // Nothing is live, so there's nowhere to go
      return;
    }

    const opts = {
      timeline,
      index,
      subIndex,
      looped,
    };

    const moveTo = direction === 'previous'
      ? getPreviousItemOrPost(opts)
      : getNextItemOrPost(opts);

    // Handle updating index/sub_index
    context.commit('ADVANCE', moveTo);

    // If moving to another social graphic or theme, then update queuedPost.
    syncQueuedPost({ timeline, ...moveTo });

    // Send changes over the socket
    context.dispatch('updateTimeline', { force: true });
  },

  updateTimeline(context, payload) {
    // Ensure sub_index is set to the proper element
    context.dispatch('syncSubIndex');

    // Then send out changes to other clients
    context.commit('UPDATE_TIMELINE', { rootState: context.rootState, ...payload });
  },

  setLayouts(context, layouts) {
    // NOTE: SET_LAYOUTS must only ever be committed through this action. The
    // action owns the deferred delta emission below, and committing the
    // mutation directly would skip it (broadcasting the intermediate state of
    // a cross-list drag) and the production-presence guard.
    context.commit('SET_LAYOUTS', { layouts, rootState: context.rootState });

    // Capture the production this layout change belongs to. The delta emission
    // is deferred so a cross-list drag's mutations settle first, but by then
    // the user may have cleared the production (CLEAR_PRODUCTION) or switched
    // to another one (SET_PRODUCTION replaces the object). Never emit a delta
    // for a production that is no longer the current one — that would
    // broadcast a bogus delta with no/incorrect production_id.
    const { production } = context.state;

    setTimeout(() => {
      if (!production?._id || context.state.production !== production) {
        return;
      }

      updateTimeline(context.state, context.rootState);
    }, 0);
  },

  /**
   * Deterministically place timeline items into a container (or the root
   * timeline) after a drag/drop. Nested-list drags fire the destination's
   * `add` before the source's `remove`, which can transiently leave the
   * dragged item in BOTH arrays, and vuedraggable's index-based splices can
   * drop or duplicate items when the DOM and model desync. This reconciles to
   * the intended state: every dragged item exists exactly once, inside the
   * target container (or at the root timeline) at `index`.
   */
  moveTimelineItemsToContainer(context, payload) {
    context.commit('MOVE_TIMELINE_ITEMS_TO_CONTAINER', payload);
  },

  /**
   * Toggle the `hidden` flag on a timeline item (e.g. graphics dropped into a
   * live graphics group should not immediately appear in the output). Resolves
   * the item by timeline_id so callers never mutate props/state directly.
   */
  setTimelineItemHidden(context, { timelineId, hidden }) {
    context.commit('SET_TIMELINE_ITEM_HIDDEN', { timelineId, hidden });
  },

  /**
   * Drop corrupt (null/empty) entries from a container's posts array. Used by
   * the drag handlers to clean up leftovers vuedraggable can leave behind
   * without mutating the container's posts directly.
   */
  cleanContainerPosts(context, { containerTimelineId }) {
    context.commit('CLEAN_CONTAINER_POSTS', { containerTimelineId });
  },

  /**
   * Fetch a Show's full item hierarchy, resolve any referenced media assets
   * and themes, flatten it into timeline entries (mirroring the Show
   * Library panel's "Add show to timeline" button), and append them to the
   * playlist. Used by the Show Builder "Add to existing production" handoff
   * (`?addShow=` query param on the production route).
   */
  async addShowToTimeline(context, { showId }) {
    const {
      state, rootState, getters, dispatch,
    } = context;

    const { production } = state;
    const isSameProduction = () => state.production === production;

    const isConnected = await waitForSocketConnected(rootState);

    if (!isConnected || !isSameProduction()) {
      console.warn('Ignoring add show to timeline: socket never connected');
      return false;
    }

    let full;
    try {
      full = await ShowsService.getShow(showId);
    } catch (err) {
      console.error(`Failed to load show ${showId}:`, err);
      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load show.',
      });

      return false;
    }

    if (!isSameProduction()) {
      return false;
    }

    const items = full.items || [];
    const assets = {};
    const themes = {};

    const assetIds = collectAssetIds(items);
    if (assetIds.length) {
      try {
        const resp = await getMultipleAssets(assetIds);
        (resp.assets || []).forEach((asset) => { assets[String(asset._id)] = asset; });
      } catch (err) {
        console.error(`Failed to load assets for show ${showId}:`, err);
      }
    }

    const themeIds = collectThemeIds(items);
    if (themeIds.length) {
      try {
        const resp = await getMultipleThemes(themeIds);
        (resp.themes || []).forEach((theme) => { themes[String(theme._id)] = theme; });
      } catch (err) {
        console.error(`Failed to load themes for show ${showId}:`, err);
      }
    }

    if (!isSameProduction()) {
      return false;
    }

    const timelineItems = flattenShowItems(items, {
      owner: production?.owner,
      optimizeImages: getters.optimizeImages,
      transition: production?.transition,
      assets,
      themes,
    });

    if (!timelineItems.length) {
      return false;
    }

    await dispatch('addLayouts', { items: timelineItems });
    // No guard needed here: addLayouts only commits synchronously, so there's
    // no async gap since the check above for state.production to have changed.

    await dispatch('getTimelineItems');

    if (!isSameProduction()) {
      return false;
    }

    dispatch('updateTimeline');
    dispatch('trackSportradarInGameElement');

    return true;
  },

  addLayouts(context, payload) {
    context.commit('ADD_LAYOUTS', { ...payload, rootState: context.rootState });
  },

  setPosts(context, payload) {
    context.commit('SET_POSTS', {
      rootState: context.rootState,
      ...payload,
    });
  },

  toggleLive(context, payload) {
    const { timelineId } = payload;

    const preview = context.rootState.preview.production;
    const { production } = context.state;

    const isAlreadyLive = (production.index === timelineId);
    const event = isAlreadyLive ? 'Off' : 'Live';
    const item = getItemFromTimeline(timelineId, production.timeline);

    // Markers are organizational only and cannot be taken live.
    if (!isAlreadyLive && item?.layout === 'marker') {
      return;
    }

    context.commit('SET_SUB_INDEX', {
      index: isAlreadyLive ? null : timelineId,
      sub_index: isAlreadyLive ? null : determineSubIndex(item),
    });

    // If nothing is live anymore...
    if (isAlreadyLive) {
      // Pause any posts that were playing
      context.dispatch('stopPostVideo');

      // Pause auto-advance
      context.dispatch('pauseTimeline');
    } else if (preview.index === timelineId && preview.videoPlaying !== production.videoPlaying) {
      // If preview is already playing,
      // then copy videoPlaying flag from there to keep video state consistent.
      context.dispatch('setVideoPlaying', preview.videoPlaying);
    }

    context.dispatch('updateTimeline');

    Analytics.toggleLiveEvent({
      event,
      item,
      rootState: context.rootState,
      rootGetters: context.rootGetters,
    });
  },

  /**
   * Force sub_index to sync with current live element
   */
  syncSubIndex(context) {
    const {
      index,
      timeline,
      playing,
    } = context.state.production;

    // Don't sync anything while timeline is playing
    // Otherwise queuing a theme post in one timeline item might cause the live one to update too.
    if (playing) { return; }

    if (!index || index === '0') {
      // Reset both
      context.commit('SET_SUB_INDEX', {
        index: null,
        sub_index: null,
      });

      return;
    }

    const item = getItemFromTimeline(index, timeline);
    context.commit('SET_SUB_INDEX', {
      index,
      sub_index: determineSubIndex(item),
    });
  },

  handleTagboardDrop(context, { e, item }) {
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;
    const { production } = context.state;
    const el = e.added.element;

    Conduit.track(
      'producer.dragged.content-from-tagboard',
      {
        production_id: production?._id,
        content_type: el?.network,
        source_tagboard: el?.from_tagboard,
        destination_theme: item?.layout,
        newIndex: e?.added?.newIndex,
        contentAdded: el,
        contentDestination: item,
      },
      {
        ownerId: production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  trackLivePostEvent(context, { item, eventSource, value}) {
    const { rootState, rootGetters } = context;
    const { user } = rootState;
    const { isStaff } = rootGetters;

  const {
    _id: productionId,
    sub_index: subIndex,
    owner,
  } = context.state.production;
    // if type is element(meaning it comes from clicking Live button) then it's a new post taken live
    const isPostAlreadyLive = (subIndex === item.timeline_id) && (eventSource !== 'element');

    const post = rootState.posts.lookupTable[item.post_id];
    const {
      connectedScreens,
    } = context?.rootState?.screens || {};
    const {
      connectedScreensCount,
      hasConnectedScreens,
    } = context.rootGetters;

    Conduit.track(
      'producer.toggled.live-post',
      {
        value: value || (isPostAlreadyLive ? 'off' : 'on'),
        production_id: productionId,
        post_id: item.post_id,
        network: item.network,
        permalink: post?.permalink,
        eventSource,
        post: normalizePostForEvents(post),
        hasConnectedScreens,
        connectedScreensCount,
        connectedScreens,
      },
      {
        ownerId: owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  toggleLivePost(context, payload) {
    const { parentTimelineId, item, eventSource } = payload;

    const {
      index,
      sub_index: subIndex,
      timeline,
    } = context.state.production;

    const postTimelineId = item.timeline_id;

    const isLayoutLive = (index === parentTimelineId);
    const isQueued = isAlreadyQueued(parentTimelineId, postTimelineId, timeline);

    if (isLayoutLive) {
      const isPostAlreadyLive = (subIndex === postTimelineId);

      context.commit('SET_SUB_INDEX', {
        index: parentTimelineId,
        sub_index: isPostAlreadyLive ? null : postTimelineId,
      });

      if (eventSource !== 'tagboard') {
        // we don't care about tracking auto-advanced posts within a tagboard multipost.
        context.dispatch('trackLivePostEvent', { item, eventSource, value: isPostAlreadyLive ? 'off' : 'on' });
      }
    }

    // Sync sub_index with queuedPost field on theme.
    // If post is already queued, then unqueue it.
    syncQueuedPost({
      timeline,
      index: parentTimelineId,
      subIndex: isQueued ? null : postTimelineId,
    });

    // Trigger updateTimeline if something above changed the state.
    if (isLayoutLive || !isQueued) {
      context.dispatch('updateTimeline', { force: true });
    }
  },

  toggleLiveVideo(context) {
    const { production } = context.state;

    if (production.videoPlaying) {
      context.commit('SET_VIDEO_PLAYING', false);
    } else {
      context.commit('SET_VIDEO_PLAYING', true);
    }

    context.dispatch('sendSettings');
  },

  playPostVideo(context) {
    if (context.state.production.videoPlaying) { return; }

    context.commit('SET_VIDEO_PLAYING', true);
    context.dispatch('updateTimeline');
  },

  stopPostVideo(context) {
    if (!context.state.production.videoPlaying) { return; }

    context.commit('SET_VIDEO_PLAYING', false);
    context.dispatch('updateTimeline');
  },

  setVideoPlaying(context, videoPlaying) {
    context.commit('SET_VIDEO_PLAYING', videoPlaying);
    context.dispatch('updateTimeline');
  },

  playTimeline(context) {
    const {user} = context.rootState;
    const { isStaff } = context.rootGetters;

    Conduit.track(
      'producer.played.playlist',
      { production_id: context.state.production._id },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );

    const {
      index,
      sub_index: subIndex,
      timeline,
    } = context.state.production;

    const item = getItemFromTimeline(index, timeline);

    // If currently on a theme without a queued post,
    // then queue the first post in the list
    if (isTheme(item?.layout) && item.posts?.length) {
      // Check that queued post exists
      const hasQueuedPost = item.queuedPost && item.posts.find((p) => p?.timeline_id === item.queuedPost);

      // If there's no queued post, or it was deleted,
      // then set queuedPost back to first post in list.
      if (!hasQueuedPost) {
        const [post] = item.posts;

        syncQueuedPost({
          timeline,
          index,
          subIndex: post?.timeline_id,
        });

        context.dispatch('updateTimeline');
      }
    }

    const isGraphic = ['graphic', 'graphics'].includes(item?.layout);

    // Auto-select first post in social graphic if nothing is already queued
    // `index` will match `sub_index` if no nested posts are queued
    if (isGraphic && (!subIndex || index === subIndex)) {
      const posts = getSocialPosts(item);

      if (posts?.length) {
        const [post] = posts;

        // Make sure the `queuedPost` field is updated on the live graphic
        syncQueuedPost({
          timeline,
          index,
          subIndex: post?.timeline_id,
        });

        context.dispatch('updateTimeline');
      }
    }

    context.commit('PLAY_TIMELINE', { rootState: context.rootState });

    // Also make sure videoPlaying flags are enabled
    context.dispatch('playPostVideo');
    context.dispatch('playPreviewVideo');

    context.dispatch('sendSettings');
  },

  pauseTimeline(context) {
    if (!context.state.production.playing) { return; }

    const {user} = context.rootState;
    const {isStaff} = context.rootGetters;

    Conduit.track(
      'producer.paused.playlist',
      { production_id: context.state.production._id },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );

    context.commit('PAUSE_TIMELINE', { rootState: context.rootState });

    // Also make sure videoPlaying flags are disabled
    context.dispatch('stopPostVideo');
    context.dispatch('stopPreviewVideo');

    context.dispatch('sendSettings');
  },

  togglePostVideo(context) {
    context.commit('TOGGLE_VIDEO_PLAYING');
    context.dispatch('sendSettings');
  },

  sendSettings(context) {
    sendSettings(context.state, context.rootState);

    // Since we aren't watching the production anymore,
    // settings changes need to be saved manually
    context.dispatch('updateProduction', {
      id: context.state.production._id,
      payload: pick(context.state.production, SettingsFields),
    });
  },

  removeTimelineItem(context, { timelineId }) {
    const { index, timeline } = context.state.production;

    const activeSportradarInGameTimelineIds = Object.keys(context.state.activeSportradarInGameElements);

    const removedItem = getItemFromTimeline(timelineId, timeline);
    if (removedItem && isContainerLayout(removedItem.layout)) {
      const nestedItems = getNestedItems(removedItem);
      nestedItems.forEach((post) => {
        if (activeSportradarInGameTimelineIds.includes(post.timeline_id)) {
          context.dispatch('removeActiveSportradarInGameElements', post.timeline_id);
        }
      });
    }

    if (activeSportradarInGameTimelineIds.includes(timelineId)) {
      context.dispatch('removeActiveSportradarInGameElements', timelineId);
    }

    context.commit('REMOVE_LAYOUT', { timelineId });

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');

    if (!timeline.length || index === timelineId) {
      // Make sure the timeline isn't still playing
      context.dispatch('pauseTimeline');
      context.dispatch('stopPostVideo');
    }
  },

  addGraphicsGroup({ commit, rootState }, payload) {
    commit('ADD_GRAPHICS_GROUP', { ...payload, rootState });
  },

  addSegment({ commit, rootState }, payload) {
    commit('ADD_SEGMENT', { ...payload, rootState });
  },

  addMarker({ commit, rootState }, payload) {
    commit('ADD_MARKER', { ...payload, rootState });
  },

  // Merge two graphics at respective indices into one "graphics" component
  mergeGraphics(context, payload) {
    context.commit('MERGE_GRAPHICS', payload);

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');
  },

  // Merge two adjacent timeline items at respective indices, grouping
  // graphics into a group or anything else into a segment
  mergeTimelineItems(context, payload) {
    context.commit('MERGE_TIMELINE_ITEMS', payload);

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');
  },

  mergeGraphicsInContainer(context, payload) {
    context.commit('MERGE_GRAPHICS_IN_CONTAINER', payload);

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');
  },

  duplicateTimelineItem(context, payload) {
    const tmp = cloneDeep(payload.item);
    if (tmp.layout !== 'marker') {
      tmp.title += ' - COPY';
    }

    setNewTimelineIds(tmp);

    context.commit('DUPLICATE_LAYOUT', { rootState: context.rootState, item: payload.item, clone: tmp });
    context.dispatch('generateShortUrls', [tmp.timeline_id]);
    context.dispatch('trackSportradarInGameElement', [tmp.timeline_id]);
  },

  duplicateTimelineItemInGroup(context, payload) {
    const tmp = cloneDeep(payload.item);
    if (tmp.layout !== 'marker') {
      tmp.title += ' - COPY';
    }

    tmp.timeline_id = crypto.randomUUID();
    if (tmp.layout === 'graphic' && Array.isArray(tmp.posts)) {
      tmp.posts.forEach((post) => {
        if (isIdOnlyPostPlaceholder(post)) {
          post.timeline_id = tmp.timeline_id;
        }
      });
    }

    context.commit('DUPLICATE_LAYOUT_IN_GROUP', { rootState: context.rootState, item: payload.item, clone: tmp });
    context.dispatch('generateShortUrls', [tmp.timeline_id]);
    context.dispatch('trackSportradarInGameElement', [tmp.timeline_id]);
  },

  removeThemePost(context, payload) {
    const {
      index,
      sub_index: subIndex,
      playing,
    } = context.state.production;

    // If removing post that is currently live, and we are playing,
    // then trigger an advance first
    if (index === payload.index && subIndex === payload.sub_index && playing) {
      context.dispatch('advance');
    }

    // Remove the actual post from the timeline theme
    context.commit('REMOVE_POST', payload);

    // Finally, let other clients know what we just did
    context.dispatch('updateTimeline');
  },

  clearTimeline(context) {
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;

    Conduit.track(
      'producer.cleared.playlist',
      { production_id: context.state.production._id },
      { ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );

    context.commit('CLEAR_TIMELINE', { rootState: context.rootState });
    setSportradarSubscriptions(context.state, {});
  },

  toggleMute(context) {
    const { muted } = context.state.production;
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;

    context.commit('TOGGLE_MUTE', { rootState: context.rootState });
    context.dispatch('sendSettings');

    Conduit.track(
      'producer.toggled.playlist-mute',
      {
        production_id: context.state.production._id,
        value: (muted) ? 'unmuted' : 'muted',
      },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  toggleLoop(context) {
    const { looped } = context.state.production;
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;

    context.commit('TOGGLE_LOOP', { rootState: context.rootState });
    context.dispatch('sendSettings');

    Conduit.track(
      'producer.toggled.playlist-loop',
      {
        production_id: context.state.production._id,
        value: (looped) ? 'disabled' : 'enabled',
        looped,
      },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  startLoader(context) {
    context.commit('START_LOADER');
  },

  stopLoader(context) {
    context.commit('STOP_LOADER');
  },

  incrementPage(context) {
    context.commit('SET_PAGE', context.state.page + 1);
  },

  /**
   * Fetch list of productions (paginated).
   * @returns {Array<Object>} List of productions.
   */
  async getProductions(context) {
    const {
      page,
      limit,
    } = context.state;

    context.commit('START_LOADER');

    const data = await Productions.getProductions({ page, limit }).finally(() => {
      context.commit('STOP_LOADER');
    });

    let { productions } = data;
    if (page > 1) {
      productions = [...context.state.productions, ...data.productions];
    }

    // De-dupe jic there was a race to load more or something
    productions = uniqBy(productions, '_id');

    context.commit('SET_TOTAL_ACTIVE_PRODUCTIONS', data.total);
    context.commit('SET_PRODUCTIONS', productions);

    return data;
  },

  async starProduction(context, productionId) {
    const result = await Productions.toggleStar(productionId);

    const production = context.state.productions.find((p) => p._id === productionId);
    production.flagged = result.starred;
    context.commit('SET_PRODUCTIONS', context.state.productions.map((p) => p._id === productionId ? production : p));

    const { flaggedProductions } = context.state;
    context.commit('SET_FLAGGED_PRODUCTIONS', production.flagged ? [production, ...flaggedProductions] : flaggedProductions.filter((p) => p._id !== productionId));
  },

  async getDeletedProductions(context) {
    context.commit('START_LOADER');

    const data = await Productions.getProductions({ deleted: true }).finally(() => {
      context.commit('STOP_LOADER');
    });

    context.commit('SET_DELETED_PRODUCTIONS', data?.productions || []);
    return data;
  },

  async getStarredProductions(context) {
    const data = await Productions.getProductions({ flagged: true });
    context.commit('SET_FLAGGED_PRODUCTIONS', data?.productions || []);
    return data;
  },

  /**
   * Save the currently open production, timeline and all.
   * This will also update `lastDeltaAt`, effectively removing any pending deltas
   * waiting to be processed. This is disruptive, and will force other connected
   * users to automatically refetch the production from the API, resetting
   * any pending changes they only have on their client.
   */
  async saveProduction(context) {
    if (context.state.saving) { return; }

    const { _id: id, title } = context.state.production;

    context.commit('SET_SAVING', true);

    await Productions.updateProduction(id, context.state.production).then(() => {
      toast.makeToast({ text: `Saved "${title}" successfully!` });
    }).catch((err) => {
      console.error('Failed to save production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Failed to save "${title}".`,
      });
    }).finally(() => {
      context.commit('SET_SAVING', false);
    });
  },

  async getProduction({ state, commit, dispatch }, productionId = state.production.id) {
    if (state.production?._id && state.production._id !== productionId) {
      // First leave the socket room for the previous production.
      // We don't want to receive extra messages if a user manually
      // navigates between productions in the browser.
      $socket.emit('leave', { production_id: state.production.id });
    }

    // Unsubscribe from existing pusher channel, if there is one
    if (state.production?._id) {
      Pusher.getClientInstance().unsubscribe(`production_${state.production._id}`);
    }

    const production = await Productions.getProduction(productionId);
    production.id = production._id; // QoL

    if (state.production?._id !== production._id) {
      setSportradarSubscriptions(state, {});
    }

    commit('SET_PRODUCTION', production);

    // Listen for notifications specific to the current production.
    const channel = Pusher.getClientInstance().subscribe(`production_${state.production._id}`);

    channel.bind('save', () => {
      // Refetch the current production whenever it's manually saved,
      // regardless of who saved it.
      // We _could_ check the userId on opts, but a single user could have multiple
      // producer clients open, so unless we update the save endpoint to
      // send us a socket ID, then we don't know who saved _exactly_.
      dispatch('getProduction');
    });

    channel.bind('delta-error', (err) => {
      console.error('Delta Error:', err);

      // NOTE: We probably want to handle this a different way,
      // but manually disconnected the socket will show the warning
      // and the user will have to reload before accessing the production again.
      // This will ensure they have the latest production and no more bad
      // deltas in their local state.
      window.$socket.disconnect();
    });

    return production;
  },

  addToActiveSportradarInGameElements({ state }, { timelineId, sport, gameId }) {
    if (!sport || !gameId) { return; }

    const previous = state.activeSportradarInGameElements[timelineId]?.games || [];
    if (previous.some((game) => game.sport === sport.toLowerCase() && game.gameId === gameId)) { return; }

    setSportradarSubscriptions(state, {
      ...state.activeSportradarInGameElements,
      [timelineId]: { games: [...previous, { sport: sport.toLowerCase(), gameId }] },
    });
  },

  removeActiveSportradarInGameElements({ state }, timelineId) {
    const next = { ...state.activeSportradarInGameElements };
    delete next[timelineId];
    setSportradarSubscriptions(state, next);
  },

  /**
   * Track the games selected by each item, including timeline field overrides.
   * Reconcile channels across the whole timeline so shared games stay subscribed.
   */
  async trackSportradarInGameElement({ state }) {
    const { production } = state;
    if (!production?.timeline) { return; }

    const request = {};
    sportradarTrackingRequests.set(state, request);
    const items = getSportradarTrackingItems(production.timeline);
    const templateIds = uniq([...items.values()].map((item) => item.template_id));
    let templates = [];
    try {
      if (templateIds.length) {
        templates = await Graphics.getMultipleTemplates(templateIds);
      }
    } catch (err) {
      // A temporary fetch failure must not tear down working subscriptions.
      console.error('Failed to fetch graphic templates for sportradar tracking:', err);
      return;
    }

    if (state.production !== production || sportradarTrackingRequests.get(state) !== request) {
      return;
    }

    if (!Array.isArray(templates)) { return; }

    const templatesById = new Map(templates.filter((template) => template?._id)
      .map((template) => [template._id, template]));
    const next = {};
    // Read current items after the request; removed items must never be resurrected.
    getSportradarTrackingItems(production.timeline).forEach((item, timelineId) => {
      const template = templatesById.get(item.template_id);
      if (!Array.isArray(template?.elements)) {
        // Partial responses preserve subscriptions for the unavailable template.
        const previous = state.activeSportradarInGameElements[timelineId];
        if (previous) { next[timelineId] = previous; }

        return;
      }

      const games = getSportradarGames(template.elements, item.fields);
      if (games.length) { next[timelineId] = { games }; }
    });

    setSportradarSubscriptions(state, next);
  },

  /**
   * Loop over timeline items and generate short URLs
   * for any QR codes found that don't already have a short URL
   * @param {Array<int>} [timelineIds] - Limit short URL generation to provided list of items
   */
  async generateShortUrls(context, timelineIds) {
    const { state, rootState } = context;
    // Check if feature is enabled
    if (process.env.QR_CODE_SWITCH !== 'ON') { return; }

    const { production } = state;
    const { timeline: initialTimeline } = production;

    if (!initialTimeline) { return; }

    let timeline = initialTimeline;

    if (timelineIds && timelineIds.length) {
      // Only include items in list of timelineIds (and parents of the items if in a group)
      timeline = timeline.filter((item) => (
        timelineIds.includes(item.timeline_id)
          || item.posts?.find((p) => timelineIds.includes(p?.timeline_id))
      ));
    }

    const opts = {
      production,
      timelineIds,
      panels: rootState.panels.lookupTable,
      templates: rootState.graphics.templatesLookupTable,
      themes: rootState.themes.lookupTable,
    };

    // Short URL generation is supplemental. A malformed QR target or shortener
    // failure must not prevent the production from finishing hydration.
    const results = await Promise.allSettled(
      timeline.map(async (post) => {
        switch (post.layout) {
          case 'panel': {
            await shortenPanelUrls({ ...opts, post });
            break;
          }

          case 'graphics': {
            await Promise.all(
              post.posts.map((p) => shortenGraphicUrls({ ...opts, post: p })),
            );

            break;
          }

          case 'graphic': {
            await shortenGraphicUrls({ ...opts, post });
            break;
          }

          case 'grid':
          case 'waterfall':
          case 'isolated':
          case 'carousel': {
            await shortenThemeUrls({ ...opts, post });
            break;
          }

          default: break;
        }
      }),
    );

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error('Unexpected error generating timeline short URLs:', {
          productionId: production.id,
          timelineId: timeline[index]?.timeline_id,
          error: result.reason,
        });
      }
    });
  },

  /**
   * Create a new production and return the result from the API request.
   * @param {Object} context - Store context.
   * @param {Object} production - Initial data for new production.
   * @returns {Promise<Object>} Created production.
   */
  async createProduction(context, production) {
    const data = await Productions.createProduction({
      ...production,
      owner: context.state.owner,
    });

    return data?.id;
  },

  async deleteProduction({ commit, rootState, rootGetters }, { production, trigger }) {
    const [err, data] = await catchify(
      Productions.deleteProduction(production._id),
    );

    const { user } = rootState;
    const { isStaff } = rootGetters;

    if (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Failed to delete ${production.title}.`,
      });

      throw err;
    }

    commit('DELETE_PRODUCTION', production);

    toast.makeToast({
      text: `${production.title} has been deleted.`,
    });

    Conduit.track(
      'producer.deleted.production',
      {
        production_id: production._id,
        tags: production.tags,
        title: production.title,
        starred: production.flagged,
        trigger,
      },
      {
        ownerId: production.owner,
        userId: user.id,
        isStaff,
      },
    );

    return data;
  },

  async restoreProduction({ commit, rootState, rootGetters }, { production, trigger }) {
    const { user}  = rootState;
    const { isStaff } = rootGetters;

    try {
      await Productions.restoreProduction(production._id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to restore production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Something went wrong. "${production.title}" may not have been deleted. Refresh to confirm.`,
      });

      throw err;
    }

    commit('RESTORE_PRODUCTION', production);
    toast.makeToast({ text: `Restored "${production.title}" successfully.` });

    Conduit.track(
      'producer.restored.production',
      {
        production_id: production._id,
        tags: production.tags,
        title: production.title,
        starred: production.flagged,
        trigger,
      },
      {
        ownerId: production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  async updateProduction(context, { id, payload }) {
    await Productions.updateProduction(id, payload);

    context.commit('MERGE_PRODUCTION_EDITS', {
      edit: {
        _id: id,
        ...payload,
      },
    });

    // If current production was updated, then send changes to other clients.
    if (id === context.state.production?._id) {
      context.dispatch('updateTimeline');
    }
  },

  async updateDimensions(context, { xDimension = false, yDimension = false }) {
    context.commit('UPDATE_DIMENSIONS', { xDimension, yDimension });

    const { _id: id } = context.state.production;
    const payload = pick(context.state.production, ['xDimension', 'yDimension']);

    context.dispatch('updateProduction', { id, payload });
  },

  setBackgroundType({ commit, state, dispatch }, backgroundType) {
    commit('SET_BACKGROUND_TYPE', backgroundType);

    const { _id: id } = state.production;
    const payload = pick(state.production, ['backgroundType']);

    dispatch('updateProduction', { id, payload });
  },

  setBackgroundAsset({ commit, state, dispatch }, backgroundAsset) {
    if (state.production.backgroundType === 'image') {
      commit('SET_BACKGROUND_IMAGE', backgroundAsset);
    } else {
      commit('SET_BACKGROUND_VIDEO', backgroundAsset);
    }

    const { _id: id } = state.production;
    const payload = pick(state.production, ['backgroundType', 'backgroundImage', 'backgroundVideo']);

    dispatch('updateProduction', { id, payload });
  },

  sendAction(context, payload) {
    $socket.emit('handleAction', { production_id: this.state.production.production._id, action: payload });
    sendToPreviewIframes('onReceiveAction', payload, { $frameTarget: true, live: true, preview: false });
  },

  registerDataModule(context, payload) {
    const { element } = payload;
    const { type } = element;

    if (type === 'sportradar') {
      if (!this.hasModule(element.id)) {
        // TODO: this is the same way sr data is stored in state with graphics for consistency
        // but it's not really necessary to store everything in state per element all of the time
        // notably when two of the same sport(nba) elements.
        this.registerModule(element.id, sportradarModule);
      }
    }
  },
};

const mutations = {
  SET_PAGE(state, page) {
    state.page = page;
  },

  SET_TIMELINE_FIELD(state, payload) {
    const {
      timelineId,
      field,
      value,
    } = payload;

    // Recursive lookup so items nested inside segments/graphics containers are found.
    const item = getItemFromTimeline(timelineId, state.production.timeline);
    if (!item) { return; }

    if (!item.fields) {
      item.fields = {};
    }

    item.fields[field] = value;
  },

  SET_TIMELINE_TITLE(state, payload) {
    const {
      timelineId,
      title,
    } = payload;

    const item = getItemFromTimeline(timelineId, state.production.timeline);
    if (!item) { return; }

    item.title = title;
  },

  CLEAR_PRODUCTION(state) {
    // Unsubscribe from pusher channel for production notifications.
    if (state.production._id) {
      Pusher.getClientInstance().unsubscribe(`production_${state.production._id}`);
    }

    setSportradarSubscriptions(state, {});
    state.production = {};
  },

  CLEAR_PRODUCTIONS(state) {
    state.productions = [];
    state.flaggedProductions = [];
    state.deletedProductions = [];
  },

  REPLACE_PLAYLIST_ITEM(state, payload) {
    state.production.timeline.splice(payload.index, 1, ...payload.items);
  },

  /**
   * Reconcile dragged timeline items to a single location after a single-item
   * drag/drop. Pulls the given items out of the root timeline and every
   * container's posts, then inserts them (once) into the target container — or
   * the root timeline when containerTimelineId is null — at `index`. This
   * corrects the duplication/deletion that nested-list drags (and the
   * add-before-remove ordering) can leave behind.
   */
  MOVE_TIMELINE_ITEMS_TO_CONTAINER(state, { items = [], containerTimelineId = null, index = 0 }) {
    const provided = (items || []).filter((item) => (
      item && item.timeline_id && Object.keys(item).length > 0
    ));

    if (!provided.length) {
      return;
    }

    const idSet = new Set(provided.map((item) => item.timeline_id));

    // Resolve a valid destination BEFORE removing anything from the source.
    // When containerTimelineId is missing, unresolved, or points to an item
    // without a posts array, fall back to the root timeline so the dragged
    // items are never pulled out of their source and then dropped.
    const target = containerTimelineId
      ? getItemFromTimeline(containerTimelineId, state.production.timeline)
      : null;

    const hasValidTarget = Boolean(target && Array.isArray(target.posts));
    const destination = hasValidTarget ? target.posts : state.production.timeline;

    // Each matching entry located before the requested index that gets removed
    // shifts later positions left by one, so the insertion index must be
    // decremented to preserve the drop position.
    let insertIndex = index;
    if (Array.isArray(destination)) {
      for (let i = 0; i < index && i < destination.length; i += 1) {
        if (idSet.has(destination[i]?.timeline_id)) {
          insertIndex -= 1;
        }
      }
    }

    const found = new Set();
    const collected = [];

    // Pull items out of the root timeline
    state.production.timeline = state.production.timeline.filter((item) => {
      if (idSet.has(item?.timeline_id)) {
        found.add(item.timeline_id);
        collected.push(item);
        return false;
      }

      return true;
    });

    // Pull items out of every container's posts (recursively)
    const pullFromContainers = (containers) => {
      containers.forEach((container) => {
        if (!container?.posts?.length) {
          return;
        }

        container.posts = container.posts.filter((post) => {
          if (idSet.has(post?.timeline_id)) {
            found.add(post.timeline_id);
            collected.push(post);
            return false;
          }

          return true;
        });

        pullFromContainers(container.posts);
      });
    };

    pullFromContainers(state.production.timeline);

    // Any provided item not found in the tree (e.g. a freshly cloned element
    // from a library tab) is added as-is.
    provided.forEach((item) => {
      if (!found.has(item.timeline_id)) {
        collected.push(item);
      }
    });

    // Keep unique items (the same object may be found in several spots)
    const uniqueItems = Array.from(
      new Map(collected.map((item) => [item.timeline_id, item])).values(),
    );

    if (!uniqueItems.length) {
      return;
    }

    // Insert into the resolved destination — the target container's posts, or
    // the root timeline when the requested container could not be resolved.
    if (hasValidTarget) {
      target.posts.splice(insertIndex, 0, ...uniqueItems);
    } else {
      state.production.timeline.splice(insertIndex, 0, ...uniqueItems);
    }
  },

  /**
   * Set the `hidden` flag on a timeline item, resolved by timeline_id (handles
   * items nested inside segments/graphics groups).
   */
  SET_TIMELINE_ITEM_HIDDEN(state, { timelineId, hidden }) {
    const item = getItemFromTimeline(timelineId, state.production.timeline);
    if (item) {
      item.hidden = hidden;
    }
  },

  /**
   * Apply a granular `updateTimelineItem` socket event to a timeline item.
   */
  SOCKET_UPDATE_TIMELINE_ITEM(state, payload) {
    const { timelineId, key, value } = payload;
    const item = getItemFromTimeline(timelineId, state.production.timeline);
    if (!item) return;

    if (isPlainObject(value)) {
      const existing = get(item, key);
      if (isPlainObject(existing)) {
        set(item, key, { ...existing, ...value });
      } else {
        set(item, key, value);
      }
    } else {
      set(item, key, value);
    }
  },

  /**
   * Apply an `addTimelineItem` socket event.
   */
  SOCKET_ADD_TIMELINE_ITEM(state, payload) {
    const {
      timelineId,
      item,
      index,
      parentTimelineId,
    } = payload;

    if (!isPlainObject(item)) return;

    if (!item.timeline_id && timelineId) {
      item.timeline_id = timelineId;
    }

    if (parentTimelineId) {
      const parent = getItemFromTimeline(parentTimelineId, state.production.timeline);
      if (!parent) return;
      if (!parent.posts) parent.posts = [];

      if (typeof index === 'number' && index >= 0 && index <= parent.posts.length) {
        parent.posts.splice(index, 0, item);
      } else {
        parent.posts.push(item);
      }
    } else {
      const timeline = state.production.timeline || [];
      if (typeof index === 'number' && index >= 0 && index <= timeline.length) {
        timeline.splice(index, 0, item);
      } else {
        timeline.push(item);
      }
    }

    // Ensure deeply nested timeline data is reactive in the Vue 3 migration.
    Reactive.createReactiveNestedObject(state.production, state.production);
  },

  /**
   * Apply a `removeTimelineItem` socket event.
   */
  SOCKET_REMOVE_TIMELINE_ITEM(state, payload) {
    removeTimelineItemById(state, payload.timelineId);
  },

  /**
   * Apply a bulk `removeTimelineItems` socket event.
   */
  SOCKET_REMOVE_TIMELINE_ITEMS(state, payload) {
    (payload.timelineIds || []).forEach((timelineId) => {
      removeTimelineItemById(state, timelineId);
    });
  },

  /**
   * Apply an `updateTimelineOrder` socket event.
   */
  SOCKET_UPDATE_TIMELINE_ORDER(state, payload) {
    const { timelineIds, parentTimelineId } = payload;
    if (!Array.isArray(timelineIds)) return;

    if (parentTimelineId) {
      const parent = getItemFromTimeline(parentTimelineId, state.production.timeline);
      if (!parent?.posts) return;

      const isPostPlaceholder = (item) => (
        isIdOnlyPostPlaceholder(item) || item?.timeline_id === parentTimelineId
      );

      const children = parent.posts.filter((item) => !isPostPlaceholder(item));
      const map = new Map(children.map((item) => [item?.timeline_id, item]));
      const reordered = timelineIds.map((id) => map.get(id)).filter(Boolean);
      const remaining = children.filter((item) => !timelineIds.includes(item?.timeline_id));
      const ordered = reordered.concat(remaining);
      let nextIndex = 0;
      // Keep legacy placeholder slots aligned with raw indexes in add/move events.
      parent.posts = parent.posts.map((item) => {
        if (isPostPlaceholder(item)) return item;
        const child = ordered[nextIndex];
        nextIndex += 1;
        return child;
      }).concat(ordered.slice(nextIndex));
    } else {
      const map = new Map(state.production.timeline.map((item) => [item?.timeline_id, item]));
      const reordered = timelineIds.map((id) => map.get(id)).filter(Boolean);
      const remaining = state.production.timeline.filter((item) => !timelineIds.includes(item?.timeline_id));
      state.production.timeline = reordered.concat(remaining);
    }
  },

  /**
   * Apply a `moveTimelineItem` socket event.
   */
  SOCKET_MOVE_TIMELINE_ITEM(state, payload) {
    moveTimelineItemById(state, payload);
  },

  /**
   * Drop corrupt (null/empty) entries from a container's posts array. `{}`
   * objects are truthy, so they survive a plain `filter(Boolean)` but have no
   * keys and break consumers that read `layout`/`fields`/`meta`.
   */
  CLEAN_CONTAINER_POSTS(state, { containerTimelineId }) {
    const container = getItemFromTimeline(containerTimelineId, state.production.timeline);
    if (container && Array.isArray(container.posts)) {
      container.posts = container.posts.filter((post) => (
        post && Object.keys(post).length > 0
      ));
    }
  },

  REPLACE_GROUP_ITEM(state, payload) {
    const group = state.production.timeline[payload.groupIndex];
    if (!group?.posts) {
      return;
    }

    // Never insert corrupt (empty) items into group/segment posts
    const items = (payload.items || []).filter(isValidTimelineItem);
    group.posts.splice(payload.index, 1, ...items);
  },

  GET_TAGS_FOR_USER(state, payload) {
    state.tagsForUser = payload;
  },

  RESTORE_PRODUCTION(state, production) {
    const { _id: id } = production;

    state.productions = [production].concat(state.productions);
    state.total += 1;

    if (production.flagged) {
      state.flaggedProductions = [production].concat(state.flaggedProductions);
    }

    state.deletedProductions = state.deletedProductions.filter((p) => p._id !== id);
  },

  DELETE_PRODUCTION(state, production) {
    const { _id: id } = production;

    state.productions = state.productions.filter((p) => p._id !== id);
    state.total -= 1;
    state.flaggedProductions = state.flaggedProductions.filter((p) => p._id !== id);
    state.deletedProductions = state.deletedProductions.concat(production);
  },

  SET_PRODUCTIONS(state, payload) {
    state.productions = payload;

    // Storing this separately so we can update it client side without triggering an autosave
    state.lastUpdated = state.production.last_updated;
  },

  SET_FLAGGED_PRODUCTIONS(state, payload) {
    state.flaggedProductions = payload;
  },

  SET_DELETED_PRODUCTIONS(state, payload) {
    state.deletedProductions = payload;
    state.loadedDeletedProductions = true;
  },

  ADD_PRODUCTION(state, payload) {
    state.productions.push(payload.production);
  },

  UPDATE_CLIENTS_IN_DETAILED_PRODUCTION(state, payload) {
    state.clientsInDetailedProduction = payload;
  },

  SET_TOTAL_ACTIVE_PRODUCTIONS(state, payload) {
    state.total = payload;
  },

  SET_PRODUCTION(state, payload) {
    state.production = payload;
    state.owner = payload.owner;
    state.production.timeline = state.production.timeline.filter(validateTimelineItem);

    // Defensively filter out null entries in nested posts arrays.
    cleanNestedPosts(state.production.timeline);

    // Dedupe timeline_ids (a duplicate is always corruption from a race in
    // delta merging; timeline_ids are unique per production).
    dedupeTimelineItems(state.production.timeline);

    setPrevProduction(payload);
  },

  SET_TIMELINE(state, payload) {
    const { delta, partialDeltas } = payload;

    // We only care if a delta or partialDeltas is provided AND a production is still loaded in state
    if ((!delta && !partialDeltas) || !state.production) {
      return;
    }

    // If index or sub_index were updated in the delta, then reset video duration
    // This is so the timers properly hide when advancing from video to non-video
    if (delta && ('index' in delta || 'sub_index' in delta)) {
      state.videoDuration = 0;
    }

    // Apply the delta to the current production
    // Prefer partialDeltas (timeline_id-based) over legacy array-index-based delta
    // to avoid race conditions that can introduce null entries in array merging
    try {
      if (partialDeltas) {
        applyPartialDeltas(state, payload);
      } else if (delta) {
        diffpatcher.patch(state.production, delta);
      }
    } catch (err) {
      console.error('Error merging delta:', delta || partialDeltas, 'into production:', state.production);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Some changes to production failed to save. If this issue persists, try refreshing the production.',
      });
    }

    // Filter out invalid timeline items (null, undefined, etc.)
    state.production.timeline = state.production.timeline.filter(validateTimelineItem);

    // Filter out null entries in nested posts arrays.
    // These can result from race conditions in array-index-based delta merging
    // that the partial deltas fix also prevents, but this is a defensive cleanup.
    cleanNestedPosts(state.production.timeline);

    // Dedupe timeline_ids. Cross-list drags (root <-> segment/group) briefly
    // hold the dragged item in both arrays (Sortable fires the destination's
    // `add` before the source's `remove`), and an echoed delta re-applied over
    // that window inserts the same item twice. timeline_ids are unique per
    // production, so a duplicate is always corruption.
    dedupeTimelineItems(state.production.timeline);

    // Ensure any new properties added to the production are reactive
    // Sadly this is still necessary, because some elements won't update properly
    Reactive.createReactiveNestedObject(state.production, state.production);

    // Store record of "previous" production for future deltas
    setPrevProduction(state.production);

    state.lastUpdated = state.production.last_updated;
  },

  UPDATE_TIMELINE(state, payload) {
    updateTimeline(state, payload.rootState, payload);
  },

  TIMELINE_SETTINGS(state, payload) {
    updatePrevProduction(payload);

    state.production = {
      ...state.production,
      ...payload,
    };
  },

  UPDATE_TIMELINE_THEME(state, payload) {
    const idx = state.production.timeline.findIndex((o) => o._id === payload.id);
    const themeIdx = payload.themes.findIndex((o) => o._id === payload.id);

    if (idx !== -1 && themeIdx !== -1) {
      Object.assign(state.production.timeline[idx], payload.themes[themeIdx]);
    }
  },

  SET_LAYOUTS(state, payload) {
    state.production.timeline = payload.layouts;

    // INVARIANT: this mutation is only ever committed through the `setLayouts`
    // action — do not commit it directly from components or other actions.
    // Delta emission is deferred in that action (never emit synchronously
    // here): emitting would broadcast the intermediate state of a cross-list
    // drag (Sortable fires the destination's `add` before the source's
    // `remove`), duplicating the dragged item for other clients and the
    // persisted production, and would bypass the production-presence guard.
  },

  ADD_LAYOUTS(state, payload) {
    const items = (payload.items || []).filter(isValidTimelineItem);

    // Add to existing group
    if (payload.parentId) {
      const parent = state.production.timeline.find((i) => i.timeline_id === payload.parentId);
      if (!parent) {
        return;
      }

      // Only allow graphic items to be added to graphics groups
      const groupItems = parent.layout === 'graphics'
        ? items.filter((item) => item.layout === 'graphic')
        : items;

      parent.posts.push(...groupItems);
      updateTimeline(state, payload.rootState);
      return;
    }

    // Add straight to playlist
    state.production.timeline = [...state.production.timeline, ...items];
    updateTimeline(state, payload.rootState);
  },

  REMOVE_LAYOUT(state, { timelineId }) {
    const liveTimelineId = state.production.index;
    const removedItem = getItemFromTimeline(timelineId, state.production.timeline);
    const removedLiveItem = Boolean(
      removedItem
      && liveTimelineId
      && hasTimelineItem(liveTimelineId, [removedItem]),
    );

    // If item has a parent container, just remove it from that container.
    // Deleting the last item in a group/segment intentionally leaves the
    // (now empty) parent in place so it can keep accepting drops.
    if (removeTimelineItemFromContainers(timelineId, state.production.timeline)) {
      if (removedLiveItem) {
        state.production.index = null;
        state.production.sub_index = null;
      }

      return;
    }

    const index = state.production.timeline.findIndex(
      (layout) => (
        layout.timeline_id === timelineId
      ),
    );

    if (index === -1) {
      return;
    }

    state.production.timeline.splice(index, 1);

    if (removedLiveItem) {
      state.production.index = null;
      state.production.sub_index = null;
    }
  },

  DUPLICATE_LAYOUT(state, payload) {
    const { timeline_id: timelineId } = payload.item;

    // Index points to the location of the element being duplicated
    // or the index of the group that element is inside of
    const index = state.production.timeline.findIndex(
      (layout) => hasTimelineItem(timelineId, [layout]),
    );

    if (index !== -1) {
      state.production.timeline.splice(index + 1, 0, payload.clone);
      updateTimeline(state, payload.rootState);
    }
  },

  DUPLICATE_LAYOUT_IN_GROUP(state, payload) {
    const { timeline_id: timelineId } = payload.item;

    // Find the parent of the item being duplicated
    const parent = findParentContainer(timelineId, state.production.timeline);

    if (!parent) {
      return;
    }

    // Find the index of the original element inside the group
    const index = parent.posts.findIndex(
      (child) => (
        child.timeline_id === timelineId
      ),
    );

    // Finally, splice in the clone right after the nested graphic in the group
    if (index !== -1) {
      parent.posts.splice(index + 1, 0, payload.clone);
      updateTimeline(state, payload.rootState);
    }
  },

  TOGGLE_QUEUED_POST(state, payload) {
    const { timelineId, postId } = payload;
    const post = getItemFromTimeline(timelineId, state.production.timeline);

    // Only set queuedPost on the root element if it's a theme
    // Graphics keep queuedPost in the fields
    if (post && post.layout !== 'graphic') {
      post.queuedPost = postId;
    }
  },

  SET_POSTS(state, payload) {
    const item = getItemFromTimeline(payload.timelineId, state.production.timeline);

    if (!item) {
      return;
    }

    // Never store corrupt (empty) entries in posts arrays
    item.posts = (payload.posts || []).filter(isValidTimelineItem);
    updateTimeline(state, payload.rootState);
  },

  REMOVE_POST(state, payload) {
    const { timeline } = state.production;
    const theme = getItemFromTimeline(payload.index, timeline);

    if (!theme) {
      return;
    }

    // Lookup index only if provided sub_index is a timeline_id
    const postIndex = typeof payload.sub_index === 'number'
      ? payload.sub_index
      : theme.posts.findIndex((p) => p.timeline_id === payload.sub_index);

    if (payload.sub_index === state.production.sub_index) {
      state.production.sub_index = null;
    }

    theme.posts.splice(postIndex, 1);
  },

  ADD_GRAPHICS_GROUP(state, {
    rootState, index = -1, posts = [],
  }) {
    const numGroups = state.production.timeline.filter((i) => i.layout === 'graphics').length;
    const groupLabel = 'Custom Scene';

    const group = {
      title: `${groupLabel} ${numGroups + 1}`,
      timeline_id: crypto.randomUUID(),
      displayTime: 5e3,
      transition: rootState.production.production.transition,
      layout: 'graphics',
      posts,
    };

    if (index === -1) {
      // Just append to end of timeline
      state.production.timeline.push(group);
    } else {
      // Splice into timeline at specific index
      state.production.timeline.splice(index, 0, group);
    }

    // Let connected clients know a group was added
    updateTimeline(state, rootState);
  },

  ADD_SEGMENT(state, { rootState, index = -1, posts = [] }) {
    const numSegments = state.production.timeline.filter((i) => i.layout === 'segment').length;

    const segment = {
      title: `Segment ${numSegments + 1}`,
      timeline_id: crypto.randomUUID(),
      displayTime: 5e3,
      transition: rootState.production.production.transition,
      layout: 'segment',
      posts,
    };

    if (index === -1) {
      state.production.timeline.push(segment);
    } else {
      state.production.timeline.splice(index, 0, segment);
    }

    updateTimeline(state, rootState);
  },

  ADD_MARKER(state, { rootState, index = -1 }) {
    const marker = {
      title: 'Marker',
      fields: {
        color: '#CC9F10',
      },
      timeline_id: crypto.randomUUID(),
      layout: 'marker',
    };

    if (index === -1) {
      state.production.timeline.push(marker);
    } else {
      state.production.timeline.splice(index, 0, marker);
    }

    updateTimeline(state, rootState);
  },

  MERGE_GRAPHICS(state, { i1, i2 }) {
    mergeGraphicsIntoGroup(state.production.timeline, i1, i2);
  },

  MERGE_TIMELINE_ITEMS(state, {
    i1, i2, sourceTimelineId,
  }) {
    const { timeline } = state.production;
    const first = timeline[i1];
    const second = timeline[i2];

    if (!first || !second) {
      return;
    }

    // Two graphics or a mix of graphics and groups merge into a graphics group
    if (['graphic', 'graphics'].includes(first.layout) && ['graphic', 'graphics'].includes(second.layout)) {
      mergeGraphicsIntoGroup(timeline, i1, i2);
      return;
    }

    // Anything else merges into a segment
    mergeIntoSegment(timeline, i1, i2, state.production, sourceTimelineId);
  },

  MERGE_GRAPHICS_IN_CONTAINER(state, { containerTimelineId, i1, i2 }) {
    const container = getItemFromTimeline(containerTimelineId, state.production.timeline);

    if (!container?.posts) {
      return;
    }

    mergeGraphicsIntoGroup(container.posts, i1, i2);
  },

  MERGE_PRODUCTION_EDITS(state, payload) {
    const { edit } = payload;

    if (state.production._id === edit._id) {
      state.production = Object.assign(state.production, edit);
    }

    const prodIdx = state.productions.findIndex((p) => p._id === edit._id);
    if (prodIdx >= 0) {
      Object.assign(state.productions[prodIdx], edit);
    }

    const flaggedIdx = state.flaggedProductions.findIndex((p) => p._id === edit._id);
    if (flaggedIdx >= 0) {
      Object.assign(state.flaggedProductions[flaggedIdx], edit);
    }
  },

  CLEAR_TIMELINE(state, payload) {
    state.production.timeline = [];
    state.production.sub_index = null;
    state.production.index = null;
    state.production.playing = false;
    state.production.videoPlaying = false;
    state.videoDuration = 0;

    updateTimeline(state, payload.rootState);
  },

  PREVIEW_UPDATE_TIMELINE(state, payload) {
    const { force } = payload;
    const preview = {
      index: payload.index,
      sub_index: payload.sub_index,
      credentials: payload.rootState.preview.credentials,
    };

    updateTimeline(state, payload.rootState, { preview, force });
  },

  SET_SUB_INDEX(state, payload) {
    const { production } = state;
    const { index, sub_index: subIndex } = payload;

    // Also clear video duration so the UI will go away
    // until the output tells us about the new video (if there is one)
    // But only if the selected item changed
    if (production.index !== index || production.sub_index !== subIndex) {
      state.videoDuration = 0;
    }

    state.production.index = index;
    state.production.sub_index = subIndex;
  },

  ADVANCE(state, payload) {
    const { index, subIndex } = payload;
    const { production } = state;

    production.index = index;
    production.sub_index = subIndex;

    // Clear videoDuration when changing active item
    state.videoDuration = 0;
  },

  PLAY_TIMELINE(state) {
    state.production.playing = true;
  },

  PAUSE_TIMELINE(state) {
    state.production.playing = false;
  },

  SET_VIDEO_PLAYING(state, videoPlaying) {
    state.production.videoPlaying = videoPlaying;
  },

  TOGGLE_VIDEO_PLAYING(state) {
    state.production.videoPlaying = !state.production.videoPlaying;
  },

  SET_DURATION(state, payload) {
    state.duration = payload;
  },

  SET_VIDEO_DURATION(state, payload) {
    state.videoDuration = payload;
  },

  TOGGLE_MUTE(state) {
    state.production.muted = !state.production.muted;
  },

  TOGGLE_LOOP(state) {
    state.production.looped = !state.production.looped;
  },

  START_LOADER(state) {
    state.loading = true;
  },

  STOP_LOADER(state) {
    state.loading = false;
  },

  SET_SAVING(state, payload) {
    state.saving = payload;
  },

  SET_OWNER(state, owner) {
    state.owner = owner;
  },

  UPDATE_DIMENSIONS(state, payload) {
    if (payload.xDimension) state.production.xDimension = payload.xDimension;
    if (payload.yDimension) state.production.yDimension = payload.yDimension;
  },

  SET_BACKGROUND_TYPE(state, backgroundType) {
    state.production.backgroundType = backgroundType;
  },

  SET_BACKGROUND_IMAGE(state, backgroundAsset) {
    state.production.backgroundImage = backgroundAsset;
  },

  SET_BACKGROUND_VIDEO(state, backgroundAsset) {
    state.production.backgroundVideo = backgroundAsset;
  },
};

const getters = {
  /* eslint-disable no-shadow */
  isSaving(state) {
    return state.saving;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
